import { useSWR } from '@ori/fetching';
import { useMemo } from 'react';
import { IntlProvider, ReactIntlErrorCode } from 'react-intl';
import { DYNAMIC_TRANSLATION_PREFIX } from '../../internals/constants';
import { mainLogger } from '../../logger';
import { fetchTranslationsWithNamespaces } from '../../utils';
import { translationsContext } from './translationsContext';
import { flattenAndConcatenateKeysWithNamespace, mapNamespacedTranslations, mapTranslationsToKeys } from './utils';
import { jsx as _jsx } from "react/jsx-runtime";
const logger = mainLogger.createChild('TranslationsProvider');
const handleIntlProviderError = err => {
  if (err.code === ReactIntlErrorCode.MISSING_DATA) {
    logger.info(err.message);
    return;
  }
  if (err.code === ReactIntlErrorCode.FORMAT_ERROR) {
    logger.warn(err.message);
    return;
  }
  if (err.code === ReactIntlErrorCode.MISSING_TRANSLATION && err.descriptor?.id?.includes(DYNAMIC_TRANSLATION_PREFIX)) {
    return;
  }
  throw err;
};

/**
 * Provider which provides translations context and internationalization for its children.
 * @returns Rendered component with translations context and internationalization provided.
 */
export const TranslationsProvider = ({
  translationsWithNamespaces,
  defaultTranslationsWithNamespaces,
  currentLanguage,
  language,
  clientFetchConfiguration,
  children
}) => {
  const usedLanguage = language ?? currentLanguage ?? '';
  const fallbackLocale = usedLanguage ? usedLanguage.slice(0, 2) : 'en';
  /**
   * Determines whenever data is needed to be fetch on client or not.
   */
  const shouldFetch = useMemo(() => !translationsWithNamespaces && clientFetchConfiguration && (!!clientFetchConfiguration.baseUrl || typeof window !== 'undefined'), [clientFetchConfiguration, translationsWithNamespaces]);
  /**
   * Fetches localization data in standalone apps using SWR with provided options.
   */
  const {
    data: localizationData
  } = useSWR(shouldFetch ? [usedLanguage, clientFetchConfiguration?.tenant, defaultTranslationsWithNamespaces] : null, async () => clientFetchConfiguration ? fetchTranslationsWithNamespaces({
    clientFetchConfiguration: {
      baseUrl: clientFetchConfiguration.baseUrl ?? window.location.origin,
      tenant: clientFetchConfiguration.tenant
    },
    language: usedLanguage,
    defaultTranslationsWithNamespaces,
    onError: clientFetchConfiguration.onError
  }) : null, {
    fallbackData: {
      translationsWithNamespaces: translationsWithNamespaces ?? defaultTranslationsWithNamespaces
    },
    suspense: true,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    revalidateOnMount: true
  });
  /**
   * Determines the data to use for the translations context.
   */
  const data = localizationData?.translationsWithNamespaces ?? translationsWithNamespaces;
  const showTranslationKeys = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('_showTranslationKeys');
  const contextValue = useMemo(() => ({
    translationsWithNamespaces: showTranslationKeys && data ? mapNamespacedTranslations(data, mapTranslationsToKeys) : data,
    defaultTranslationsWithNamespaces: showTranslationKeys ? mapNamespacedTranslations(defaultTranslationsWithNamespaces, mapTranslationsToKeys) : defaultTranslationsWithNamespaces,
    currentLanguage: usedLanguage,
    language: usedLanguage,
    clientFetchConfiguration: clientFetchConfiguration ?? undefined,
    showTranslationKeys
  }), [data, defaultTranslationsWithNamespaces, usedLanguage, clientFetchConfiguration, showTranslationKeys]);
  const reactIntlData = flattenAndConcatenateKeysWithNamespace(data ? showTranslationKeys ? mapNamespacedTranslations(data, mapTranslationsToKeys) : data : {});
  return /*#__PURE__*/_jsx(translationsContext.Provider, {
    value: contextValue,
    children: /*#__PURE__*/_jsx(IntlProvider, {
      locale: usedLanguage,
      defaultLocale: fallbackLocale,
      messages: reactIntlData,
      onError: handleIntlProviderError,
      children: children
    })
  });
};
//# sourceMappingURL=TranslationsProvider.js.map